import type { Company, CompanyForm } from '../../services/companyService';

/**
 * ตรรกะฟอร์มบริษัท (ไม่มี UI) — ย้ายจากโมดัลใน CompanyPage (28 ต.ค. 2026) ให้หน้าเพิ่ม/แก้ไข
 * (CompanyFormPage) ใช้ชุดเดียว ไม่ต้องคัดลอก validation
 */

/** ฟอร์มเปล่าตอน "เพิ่มบริษัทใหม่" — ค่าเดียวกับ handleAddCompany ของโมดัลเดิม
 *  billing_day / payment_terms / due_day_of_next_month ไม่ใส่ (undefined = service ไม่ส่ง key → backend ใช้ค่า default 1/30/NULL)
 *  แต่ select แสดง 1 / 30 / "ไม่กำหนด" ตรงกับค่าที่ backend จะตั้ง */
export const emptyCompanyForm = (code = ''): CompanyForm => ({
  code,
  name: '',
  taxId: '',
  address: '',
  phone: '',
  email: '',
  contactPerson: '',
  depositSettings: {
    deposit_amount: 40000, // ตั้งค่า default เป็น 40000
  },
  default_wht_rate: 0,
  sales_person_id: undefined,
  customer_type: undefined,
  is_active: true,
});

/** บริษัทจาก API (camelCase บางช่อง snake บางช่อง) → ค่าในฟอร์ม · ใช้ทั้งหน้าดูและหน้าแก้ไข */
export const companyToForm = (company: Company): CompanyForm => {
  // ใช้ salesPersonId จาก backend หรือ sales_person_id ถ้ามี
  const salesPersonId = company.salesPersonId || company.sales_person_id;
  const isActive = company.isActive !== undefined ? company.isActive : (company.is_active ?? true);
  return {
    code: company.code || '',
    name: company.name || '',
    taxId: company.taxId || '',
    address: company.address || '',
    phone: company.phone || '',
    email: company.email || '',
    contactPerson: company.contactPerson || '',
    depositSettings: {
      // prefill 40000 เฉพาะเมื่อ backend ยังไม่เคยตั้งค่า (null/undefined) — เก็บค่า 0 ("ไม่จำกัด") ที่ตั้งไว้แล้ว
      deposit_amount: company.deposit_amount ?? 40000,
    },
    default_wht_rate: company.default_wht_rate ?? company.defaultWhtRate ?? 0,
    billing_day: company.billing_day ?? company.billingDay ?? 1,
    payment_terms: company.payment_terms ?? company.paymentTerms ?? 30,
    // เดิมโมดัลโหมดแก้ไขตกหล่น field นี้ → ช่อง "วันครบกำหนดชำระ" ขึ้นว่างทุกครั้งที่กดแก้ไข ทั้งที่บริษัทมีค่าอยู่จริง
    // ผู้ใช้เห็นว่างก็นึกว่ายังไม่ได้ตั้ง แล้วกรอกทับ · ตอนนี้หน้าดูและหน้าแก้ไขใช้ mapper ตัวเดียวกัน
    due_day_of_next_month: company.due_day_of_next_month ?? company.dueDayOfNextMonth ?? null,
    sales_person_id: salesPersonId,
    customer_type: company.customer_type || company.customerType || undefined,
    is_active: isActive,
  } as CompanyForm;
};

/** payload ตอนแก้ไข — ตัด depositSettings ทิ้งเมื่อผู้ใช้ไม่ได้แตะช่องมัดจำ
 *  API คืน deposit_amount ที่ใน DB เป็น NULL ออกมาเป็น 0 (ทั้งคู่ = "ไม่จำกัด") → ฟอร์มส่ง 0 กลับทุกครั้งที่กดบันทึก
 *  DB จึงถูกเขียน NULL→0 และไทม์ไลน์ขึ้น "ยอดค่ามัดจำเก็บสูงสุด: - → 0.00 บาท" ทั้งที่ไม่ได้แก้ (E2E R1 28 ต.ค. 2026)
 *  ไม่ส่ง key = backend ไม่แตะคอลัมน์ (exclude_unset) · แก้จริงเท่านั้นถึงส่ง */
export const companyUpdatePayload = (formData: CompanyForm, initial: CompanyForm | null): CompanyForm => {
  const before = initial?.depositSettings?.deposit_amount;
  const after = formData.depositSettings?.deposit_amount;
  if (initial && Number(before) === Number(after)) {
    return { ...formData, depositSettings: undefined } as CompanyForm;
  }
  return formData;
};

/** ตรวจฟอร์มฝั่ง FE — คืน {} เมื่อผ่าน · key = name ของช่อง (ใช้ focus/scroll หาช่องแรกที่ผิด) */
export const validateCompanyForm = (formData: CompanyForm, mode: 'create' | 'edit'): Record<string, string> => {
  const errors: Record<string, string> = {};

  // รหัสบริษัทจำเป็นเฉพาะเมื่อสร้างใหม่
  if (mode === 'create' && !formData.code?.trim()) {
    errors.code = 'กรุณาใส่รหัสบริษัท';
  }
  if (!formData.name?.trim()) {
    errors.name = 'กรุณาใส่ชื่อบริษัท';
  }
  // taxId ไม่บังคับกรอก
  if (!formData.address?.trim()) {
    errors.address = 'กรุณาใส่ที่อยู่';
  }

  // KD3 S2496 (FLOW-MD-013): ขอบเขตเดียวกับ schema (deposit_amount ge=0 le=99,999,999.99) — เดิมมีแค่ HTML min="0"
  // ที่ไม่บล็อก (ปุ่มบันทึกไม่ใช่ native submit) → -0.01 / 100,000,000 ไปโดน 422 แล้วช่องไม่ขึ้น error เลย มีแต่ toast
  const deposit = formData.depositSettings?.deposit_amount;
  if (deposit !== undefined && deposit !== null && !Number.isNaN(Number(deposit))) {
    if (Number(deposit) < 0) errors.depositAmount = 'ยอดค่ามัดจำต้องไม่ติดลบ';
    else if (Number(deposit) > 99999999.99) errors.depositAmount = 'ยอดค่ามัดจำต้องไม่เกิน 99,999,999.99 บาท';
  }

  return errors;
};

/** token If-Match ของ PUT (P-S468-1) — companies API คืน camelCase (updatedAt/createdAt) · BUG-S601-IFMATCHCAMEL */
export const companyIfMatch = (company: Company): string | undefined => {
  const c = company as Company & { updated_at?: string; created_at?: string };
  return c.updated_at || c.updatedAt || c.created_at || c.createdAt || undefined;
};

/** ป้ายสำหรับหน้าดู (ค่าเดียวกับ option ของ select ในฟอร์ม) */
export const WHT_RATE_LABELS: Record<number, string> = {
  0: '0% - ไม่หัก ณ ที่จ่าย',
  1: '1% - ค่าขนส่ง',
  2: '2% - ค่าโฆษณา',
  3: '3% - ค่าบริการ',
  5: '5% - ค่าเช่า',
};
export const PAYMENT_TERM_OPTIONS = [5, 7, 15, 30, 45, 60, 90] as const;
export const DUE_DAY_OPTIONS = [1, 5, 10, 15, 20, 25] as const;
export const CUSTOMER_TYPE_OPTIONS = [
  'บริษัท รปภ.',
  'นิติบุคคลหมู่บ้าน',
  'นิติบุคคลอาคารชุด',
  'บริษัทเช่าโดยตรง',
  'บุคคลธรรมดา',
] as const;

/** ชื่อฟิลด์ภาษาไทยของไทม์ไลน์ประวัติ (key = คอลัมน์ backend ใน details.changes / "fields: …") */
export const COMPANY_FIELD_LABELS: Record<string, string> = {
  code: 'รหัสบริษัท',
  name: 'ชื่อบริษัท',
  tax_id: 'เลขประจำตัวผู้เสียภาษี',
  address: 'ที่อยู่',
  contact_person: 'ผู้ติดต่อ',
  phone: 'เบอร์โทรศัพท์',
  email: 'อีเมล',
  deposit_amount: 'ยอดค่ามัดจำเก็บสูงสุด',
  default_wht_rate: 'อัตราหัก ณ ที่จ่าย',
  billing_day: 'วันออกบิล',
  payment_terms: 'ระยะเวลาชำระเงิน (วัน)',
  due_day_of_next_month: 'วันครบกำหนดชำระ (ของเดือนถัดไป)',
  sales_person_id: 'พนักงานขายที่ดูแล',
  customer_type: 'ประเภทลูกค้า',
  is_active: 'สถานะ',
  min_required_signers: 'จำนวนผู้ลงนามขั้นต่ำ',
};
